/**
 * The reply text under the primary action: the subject, the body with the gaps to fill
 * marked, and the "Copied" status. Rendered only when a draft exists. "Edit text" (under
 * More) turns the body into a box; edits stay on this page and are never saved or sent.
 * "Copy the reply" copies exactly this text with the subject.
 */

import { Chip, Textarea, Typography } from "@norican/react";

import { type AnswerDraft, languageName, splitPlaceholders } from "@/lib/api";

/** Under the box while editing: edits are not saved anywhere. */
export const EDITS_STAY_HERE_TEXT =
  "Your edits stay on this page only. I2Q does not save them: copy the reply before you leave this case.";

/**
 * The language line of the reply card (tester round 5, 12 Oct 2026: an English reply to
 * a translated mail, with no word on why). The reply follows the desk's rule, not the
 * customer's language; the sign-off has no name because Outlook adds the signature.
 */
export function replyLanguageText(draft: Pick<AnswerDraft, "language">): string | null {
  const name = languageName(draft.language);
  if (!name) {
    return null;
  }
  return `Written in ${name}, the reply language of your desk. Outlook adds your signature under the sign-off.`;
}

function texts(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
    : [];
}

/** The placeholders of a draft still in `text`. */
export function openPlaceholders(draft: Pick<AnswerDraft, "placeholders">, text: string): string[] {
  return texts(draft.placeholders).filter((token) => text.includes(token));
}

export interface ReplyTextProps {
  draft: AnswerDraft;
  /** The text as shown (the draft's body, or the viewer's edit of it). */
  text: string;
  editing: boolean;
  onChange: (text: string) => void;
  copied: "yes" | "failed" | null;
  /** True when the draft was made before the case changed. */
  stale?: boolean;
}

export function ReplyText({
  draft,
  text,
  editing,
  onChange,
  copied,
  stale = false,
}: ReplyTextProps) {
  const open = openPlaceholders(draft, text);
  const edited = text !== (draft.body ?? "");
  return (
    <div className="flex min-w-0 flex-col gap-2" data-reply-text={stale ? "stale" : "ready"}>
      {draft.subject && (
        <Typography variant="body-sm" className="text-fg break-words">
          <span className="text-fg-subtle">Subject: </span>
          {draft.subject}
        </Typography>
      )}
      {editing ? (
        <Textarea
          rows={10}
          autoResize
          value={text}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Reply text"
        />
      ) : (
        <p className="text-fg bg-bg-inset rounded-md p-3 text-sm break-words whitespace-pre-wrap">
          {splitPlaceholders(text, open).map((segment, index) =>
            segment.placeholder ? (
              <span
                key={index}
                className="bg-warning-subtle text-warning-fg rounded px-0.5 font-semibold"
              >
                {segment.text}
              </span>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
          )}
        </p>
      )}
      {replyLanguageText(draft) && (
        <Typography variant="caption" tone="muted" data-reply-language={draft.language ?? ""}>
          {replyLanguageText(draft)}
        </Typography>
      )}
      {open.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <Typography as="span" variant="caption" tone="subtle">
            Fill in before you send
          </Typography>
          {open.map((token) => (
            <Chip key={token} tone="warning" size="sm">
              {token}
            </Chip>
          ))}
        </div>
      )}
      {(editing || edited) && (
        <Typography variant="caption" tone="muted" data-edits-local>
          {EDITS_STAY_HERE_TEXT}
        </Typography>
      )}
      {copied === "yes" && (
        <Typography variant="caption" tone="muted" role="status">
          Copied with the subject. Paste it into your reply in Outlook.
        </Typography>
      )}
      {copied === "failed" && (
        <Typography variant="caption" className="text-danger-fg" role="alert">
          Copy did not work. Mark the text and copy it yourself.
        </Typography>
      )}
    </div>
  );
}
